// src/pages/Reports/CategoryEconomicsReportPage.js
//
// Category Economics Report UI
// ============================================================================
// PURPOSE
// -------
// This page presents category-level economic analytics based on completed and
// checked visits.
//
// MAIN BUSINESS QUESTIONS
// -----------------------
// - Which categories generate the most revenue?
// - Which categories consume the most booked time?
// - Which categories have the best revenue per booked hour?
// - Which categories have the best average ticket?
// - Which categories dominate the business mix?
//
// WHY THIS REPORT IS IMPORTANT
// ----------------------------
// Service-level profitability is excellent for detailed decisions, but
// management often needs a more strategic view of the business mix.
//
// This page aggregates business performance one level above services and helps
// answer questions such as:
// - which segment of the business is strongest?
// - which category dominates time but not revenue?
// - which category may deserve stronger promotion or pricing review?
//
// CURRENT DATA EXPECTATIONS
// -------------------------
// Backend payload shape:
//
// {
//   period: { from, to },
//   filters: { employee_ids: [...] },
//   methodology: { note_keys: [...], notes: [...] },
//   summary: {
//     completed_visits_count,
//     distinct_categories_count,
//     total_revenue,
//     total_booked_minutes
//   },
//   rows: [
//     {
//       category,
//       total_visits,
//       total_booked_minutes,
//       total_revenue,
//       avg_revenue_per_visit,
//       avg_duration_minutes,
//       revenue_per_booked_hour,
//       distinct_services_count,
//       top_services,
//       share_of_total_revenue,
//       share_of_total_booked_minutes
//     }
//   ]
// }
//
// UI GOALS
// --------
// 1) Keep the page visually aligned with the other report pages.
// 2) Support local search and local sorting without extra backend round-trips.
// 3) Show both high-level summary and category ranking table.
// 4) Expose top services per category to make the category view more actionable.
// 5) Add strong executive visualizations that make category economics readable
//    within seconds rather than forcing the user to interpret only a wide table.
//

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchCategoryEconomicsReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from "recharts";

import "./CategoryEconomicsReportPage.css";

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Format money with fixed two decimals.
 *
 * We intentionally keep management formatting stable and compact.
 */
function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

/**
 * Format minute values.
 */
function formatMinutes(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0";
  return n.toFixed(1);
}

/**
 * Format integer-like counters safely.
 */
function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n));
}

/**
 * Format generic decimal values.
 */
function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

/**
 * Format ratio 0..1 as percentage text.
 */
function formatPercentFromRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0%";
  return `${(n * 100).toFixed(1)}%`;
}

/**
 * Format already-percent-like values such as cumulative share in 0..100 range.
 */
function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0%";
  return `${n.toFixed(1)}%`;
}

/**
 * Convert top_services array into compact text.
 *
 * Backend shape example:
 * [
 *   {
 *     service,
 *     total_visits,
 *     total_booked_minutes,
 *     total_revenue
 *   }
 * ]
 */
function formatTopServices(items) {
  if (!Array.isArray(items) || items.length === 0) return "—";

  return items.map((x) => `${x.service} (${x.total_visits})`).join(", ");
}

/* -------------------------------------------------------------------------- */
/* Chart palette + executive helpers                                          */
/* -------------------------------------------------------------------------- */

/**
 * Stable local palette for category economics.
 *
 * Meaning:
 * - revenue family: blue
 * - efficient / excellent categories: green
 * - cumulative concentration: amber
 * - weak categories: red/orange accents
 */
const CHART_COLORS = Object.freeze({
  revenue: "#2563eb",
  revenueLight: "#60a5fa",
  efficiency: "#16a34a",
  efficiencyLight: "#4ade80",
  cumulative: "#f59e0b",
  weak: "#f97316",
  poor: "#dc2626",
  neutral: "#94a3b8",
  grid: "#e5e7eb",
  axis: "#6b7280",
  tooltipBg: "#ffffff",
  tooltipBorder: "#d1d5db",
});

/**
 * Bubble-size helper for executive scatter chart.
 *
 * We keep the visual range constrained so large categories do not completely
 * dominate the rest of the matrix.
 */
function bubbleSizeFromValue(value, minValue, maxValue) {
  const v = Number(value) || 0;
  const min = Number(minValue) || 0;
  const max = Number(maxValue) || 0;

  if (max <= min) return 140;

  const ratio = (v - min) / (max - min);
  return 80 + ratio * 260;
}

/**
 * Executive class for category visuals.
 *
 * This is deliberately managerial and heuristic.
 * It is not a scientific classification model.
 *
 * Interpretation:
 * - star:
 *     large category and strong hourly economics
 * - efficient:
 *     very good hourly economics even without giant scale
 * - volume:
 *     large category, but softer hourly efficiency
 * - weak:
 *     small and economically weak
 * - standard:
 *     everything in the middle
 */
function getCategoryExecutiveClass(row) {
  const totalRevenue = Number(row?.total_revenue || 0);
  const revenuePerBookedHour = Number(row?.revenue_per_booked_hour || 0);
  const totalVisits = Number(row?.total_visits || 0);

  if (totalRevenue >= 1500 && revenuePerBookedHour >= 140) return "star";
  if (revenuePerBookedHour >= 150) return "efficient";
  if (totalRevenue >= 1500 && revenuePerBookedHour < 95) return "volume";
  if (totalRevenue < 400 && revenuePerBookedHour < 90 && totalVisits <= 4) return "weak";
  return "standard";
}

function getCategoryExecutiveColor(executiveClass) {
  switch (executiveClass) {
    case "star":
      return CHART_COLORS.efficiency;
    case "efficient":
      return "#0ea5e9";
    case "volume":
      return CHART_COLORS.cumulative;
    case "weak":
      return CHART_COLORS.poor;
    case "standard":
    default:
      return CHART_COLORS.revenue;
  }
}

function getCategoryExecutiveClassLabel(executiveClass, t) {
  /**
   * Keep executive classification codes stable for chart logic, but translate
   * the visible label. This makes future backend/reporting changes safer: the
   * frontend can continue comparing technical class codes while the UI remains
   * language-aware.
   */
  switch (executiveClass) {
    case "star":
      return t("reports:categoryEconomics.executiveClasses.star");
    case "efficient":
      return t("reports:categoryEconomics.executiveClasses.efficient");
    case "volume":
      return t("reports:categoryEconomics.executiveClasses.volume");
    case "weak":
      return t("reports:categoryEconomics.executiveClasses.weak");
    case "standard":
    default:
      return t("reports:categoryEconomics.executiveClasses.standard");
  }
}

function getCategoryExecutiveClassExplanation(executiveClass, t) {
  switch (executiveClass) {
    case "star":
      return t("reports:categoryEconomics.executiveExplanations.star");
    case "efficient":
      return t("reports:categoryEconomics.executiveExplanations.efficient");
    case "volume":
      return t("reports:categoryEconomics.executiveExplanations.volume");
    case "weak":
      return t("reports:categoryEconomics.executiveExplanations.weak");
    case "standard":
    default:
      return t("reports:categoryEconomics.executiveExplanations.standard");
  }
}

/* -------------------------------------------------------------------------- */
/* Rich chart tooltip blocks                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Rich tooltip for the Category Excellence Matrix.
 *
 * This is important because a scatter plot without a clear tooltip forces the
 * user to guess what the dot really means.
 */
function CategoryExcellenceTooltip({ active, payload }) {
  const { t } = useTranslation(["common", "reports"]);
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">{row.label}</div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:categoryEconomics.totalRevenue")}</span>
          <strong>{formatMoney(row.total_revenue)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:categoryEconomics.revenuePerBookedHour")}</span>
          <strong>{formatMoney(row.revenue_per_booked_hour)} PLN/h</strong>
        </div>
        <div>
          <span>{t("reports:categoryEconomics.totalVisits")}</span>
          <strong>{formatCount(row.total_visits)}</strong>
        </div>
        <div>
          <span>{t("reports:categoryEconomics.totalBookedMinutes")}</span>
          <strong>{formatMinutes(row.total_booked_minutes)} min</strong>
        </div>
        <div>
          <span>{t("reports:categoryEconomics.avgRevenuePerVisit")}</span>
          <strong>{formatMoney(row.avg_revenue_per_visit)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:categoryEconomics.executiveClass")}</span>
          <strong>{getCategoryExecutiveClassLabel(row.executiveClass, t)}</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {getCategoryExecutiveClassExplanation(row.executiveClass, t)}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const CategoryEconomicsReportPage = () => {
  /**
   * UI translation hook.
   *
   * This page intentionally keeps the backend report contract unchanged.
   * Backend data remains language-neutral and code-like, while all visible UI
   * labels, report explanations, chart captions and error messages are resolved
   * through the common + reports namespaces.
   */
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  /**
   * Role-based evaluation modes.
   *
   * Same reporting principle as in your other pages.
   */
  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole]);

  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  /**
   * Local page filters.
   *
   * - userIds are sent to backend
   * - searchText / sortBy / sortDir are local only
   */
  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
    searchText: "",
    sortBy: "total_revenue",
    sortDir: "desc",
  }));

  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Track what range is actually loaded.
   *
   * This keeps UX aligned with the rest of the reports where picker changes
   * do not auto-reload until Generate is pressed.
   */
  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  /* ------------------------------------------------------------------------ */
  /* Keep userIds aligned for role === "user"                                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  /* ------------------------------------------------------------------------ */
  /* Load users for employee filter                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[CategoryEconomicsReportPage] ❌ Failed to fetch users:", err);
        setError(t("reports:categoryEconomics.errors.failedToLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  /* ------------------------------------------------------------------------ */
  /* Current user display helper                                              */
  /* ------------------------------------------------------------------------ */

  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser", { defaultValue: "Current user" });
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Fetch report                                                             */
  /* ------------------------------------------------------------------------ */

  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = await fetchCategoryEconomicsReport(from, to, userIds);
        setReport(payload || null);

        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error("[CategoryEconomicsReportPage] ❌ Error fetching report:", err);
        setError(t("reports:categoryEconomics.errors.failedToFetch"));
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  /* ------------------------------------------------------------------------ */
  /* Derived data                                                             */
  /* ------------------------------------------------------------------------ */

  const rows = useMemo(() => {
    return Array.isArray(report?.rows) ? report.rows : [];
  }, [report]);

  const methodologyNotes = useMemo(() => {
    const methodology = report?.methodology || {};
    const reportKind = methodology.report_kind || "category_economics";

    /**
     * Preferred path for the i18n migration:
     * backend sends stable note_keys, frontend owns the visible language.
     * The legacy notes array remains supported so this page continues to work
     * against older backend payloads during staged deployments.
     */
    if (Array.isArray(methodology.note_keys) && methodology.note_keys.length > 0) {
      return methodology.note_keys.map((noteKey) => {
        const translatedNote = t(
          `reports:methodology.${reportKind}.notes.${noteKey}`,
          { defaultValue: "" }
        );

        return translatedNote || String(noteKey);
      });
    }

    return Array.isArray(methodology.notes) ? methodology.notes : [];
  }, [report, t]);

  const summary = report?.summary || {
    completed_visits_count: 0,
    distinct_categories_count: 0,
    total_revenue: "0.00",
    total_booked_minutes: 0,
  };

  const hasReportData = rows.length > 0;

  /* ------------------------------------------------------------------------ */
  /* Local search + sorting                                                   */
  /* ------------------------------------------------------------------------ */

  const filteredRows = useMemo(() => {
    const searchText = String(filters.searchText || "").trim().toLowerCase();

    if (!searchText) return rows;

    return rows.filter((row) => {
      const category = String(row.category || "").toLowerCase();
      const topServices = formatTopServices(row.top_services).toLowerCase();

      return category.includes(searchText) || topServices.includes(searchText);
    });
  }, [rows, filters.searchText]);

  const sortedRows = useMemo(() => {
    const out = [...filteredRows];
    const sortBy = filters.sortBy || "total_revenue";
    const sortDir = filters.sortDir === "asc" ? "asc" : "desc";

    out.sort((a, b) => {
      let cmp = 0;

      switch (sortBy) {
        case "category":
          cmp = String(a.category || "").localeCompare(String(b.category || ""));
          break;

        case "revenue_per_booked_hour":
          cmp =
            Number(a.revenue_per_booked_hour || 0) -
            Number(b.revenue_per_booked_hour || 0);
          break;

        case "avg_revenue_per_visit":
          cmp =
            Number(a.avg_revenue_per_visit || 0) -
            Number(b.avg_revenue_per_visit || 0);
          break;

        case "total_visits":
          cmp = Number(a.total_visits || 0) - Number(b.total_visits || 0);
          break;

        case "total_booked_minutes":
          cmp =
            Number(a.total_booked_minutes || 0) -
            Number(b.total_booked_minutes || 0);
          break;

        case "total_revenue":
        default:
          cmp = Number(a.total_revenue || 0) - Number(b.total_revenue || 0);
          break;
      }

      if (cmp === 0) {
        cmp = String(a.category || "").localeCompare(String(b.category || ""));
      }

      return sortDir === "asc" ? cmp : -cmp;
    });

    return out;
  }, [filteredRows, filters.sortBy, filters.sortDir]);

  /* ------------------------------------------------------------------------ */
  /* Highlight cards                                                          */
  /* ------------------------------------------------------------------------ */

  const topRevenueCategory = useMemo(() => {
    if (!rows.length) return null;
    return [...rows].sort(
      (a, b) => Number(b.total_revenue || 0) - Number(a.total_revenue || 0)
    )[0];
  }, [rows]);

  const topHourlyCategory = useMemo(() => {
    if (!rows.length) return null;
    return [...rows].sort(
      (a, b) =>
        Number(b.revenue_per_booked_hour || 0) -
        Number(a.revenue_per_booked_hour || 0)
    )[0];
  }, [rows]);

  const topAvgTicketCategory = useMemo(() => {
    if (!rows.length) return null;
    return [...rows].sort(
      (a, b) =>
        Number(b.avg_revenue_per_visit || 0) -
        Number(a.avg_revenue_per_visit || 0)
    )[0];
  }, [rows]);

  /* ------------------------------------------------------------------------ */
  /* Chart row normalization                                                  */
  /* ------------------------------------------------------------------------ */

  /**
   * Separate chart-oriented shape from raw backend rows.
   *
   * This helps keep visual logic clean and robust.
   */
  const categoryChartRows = useMemo(() => {
    return rows.map((row, index) => ({
      ...row,
      id: `${row?.category || "category"}-${index}`,
      label: row?.category || t("reports:categoryEconomics.unknownCategory"),

      total_revenue: Number(row?.total_revenue || 0),
      total_booked_minutes: Number(row?.total_booked_minutes || 0),
      total_visits: Number(row?.total_visits || 0),
      avg_revenue_per_visit: Number(row?.avg_revenue_per_visit || 0),
      revenue_per_booked_hour: Number(row?.revenue_per_booked_hour || 0),
      share_of_total_revenue: Number(row?.share_of_total_revenue || 0),
    }));
  }, [rows, t]);

  /* ------------------------------------------------------------------------ */
  /* Executive chart datasets                                                 */
  /* ------------------------------------------------------------------------ */

  /**
   * Category Excellence Matrix
   *
   * X = total revenue
   * Y = revenue per booked hour
   * bubble size = visit volume
   */
  const categoryExcellenceMatrixData = useMemo(() => {
    if (!categoryChartRows.length) return [];

    const visitValues = categoryChartRows.map((row) => Number(row.total_visits) || 0);
    const minVisits = Math.min(...visitValues);
    const maxVisits = Math.max(...visitValues);

    return categoryChartRows.map((row) => {
      const executiveClass = getCategoryExecutiveClass(row);

      return {
        ...row,
        x: row.total_revenue,
        y: row.revenue_per_booked_hour,
        z: bubbleSizeFromValue(row.total_visits, minVisits, maxVisits),
        executiveClass,
        color: getCategoryExecutiveColor(executiveClass),
      };
    });
  }, [categoryChartRows]);

  /**
   * Category Revenue Concentration / Pareto
   *
   * Bars = revenue by category
   * Line = cumulative revenue share %
   */
  const categoryParetoData = useMemo(() => {
    const sorted = [...categoryChartRows].sort(
      (a, b) =>
        Number(b.total_revenue || 0) - Number(a.total_revenue || 0) ||
        String(a.label || "").localeCompare(String(b.label || ""))
    );

    const totalRevenue = sorted.reduce(
      (sum, row) => sum + (Number(row.total_revenue) || 0),
      0
    );

    let runningRevenue = 0;

    return sorted.slice(0, 20).map((row) => {
      runningRevenue += Number(row.total_revenue || 0);

      return {
        ...row,
        cumulative_revenue_share_percent:
          totalRevenue > 0 ? (runningRevenue / totalRevenue) * 100 : 0,
      };
    });
  }, [categoryChartRows]);

  /**
   * Top Category Efficiency
   *
   * Horizontal ranking by revenue per booked hour.
   */
  const categoryEfficiencyRankingData = useMemo(() => {
    return [...categoryChartRows]
      .sort(
        (a, b) =>
          Number(b.revenue_per_booked_hour || 0) - Number(a.revenue_per_booked_hour || 0) ||
          String(a.label || "").localeCompare(String(b.label || ""))
      )
      .slice(0, 10)
      .reverse();
  }, [categoryChartRows]);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="category-economics-page">
      <div className="category-economics-header">
        <div className="header-left">
          <h1>{t("reports:categoryEconomics.title")}</h1>
          <p className="header-hint">{t("reports:categoryEconomics.headerHint")}</p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="filters-bar">
        <div className="filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}

          <div className="filter-item filter-item-search">
            <label className="filter-label">{t("reports:common.search")}</label>
            <input
              type="text"
              className="search-input"
              value={filters.searchText}
              placeholder={t("reports:categoryEconomics.searchPlaceholder")}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  searchText: e.target.value,
                }))
              }
            />
          </div>

          <div className="filter-item filter-item-sort">
            <label className="filter-label">{t("reports:categoryEconomics.sortBy")}</label>
            <select
              className="sort-select"
              value={filters.sortBy}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortBy: e.target.value,
                }))
              }
            >
              <option value="total_revenue">{t("reports:categoryEconomics.totalRevenue")}</option>
              <option value="revenue_per_booked_hour">{t("reports:categoryEconomics.revenuePerBookedHour")}</option>
              <option value="avg_revenue_per_visit">{t("reports:categoryEconomics.avgRevenuePerVisit")}</option>
              <option value="total_visits">{t("reports:categoryEconomics.totalVisits")}</option>
              <option value="total_booked_minutes">{t("reports:categoryEconomics.totalBookedMinutes")}</option>
              <option value="category">{t("reports:categoryEconomics.categoryName")}</option>
            </select>
          </div>

          <div className="filter-item filter-item-sortdir">
            <label className="filter-label">{t("reports:categoryEconomics.direction")}</label>
            <select
              className="sort-select"
              value={filters.sortDir}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortDir: e.target.value,
                }))
              }
            >
              <option value="desc">{t("reports:categoryEconomics.descending")}</option>
              <option value="asc">{t("reports:categoryEconomics.ascending")}</option>
            </select>
          </div>
        </div>

        <div className="filters-actions">
          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>
        </div>
      </div>

      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : report ? (
          <>
            <div className="summary-grid">
              <div className="summary-card">
                <span className="summary-label">{t("reports:categoryEconomics.completedVisits")}</span>
                <span className="summary-value">
                  {formatCount(summary.completed_visits_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:categoryEconomics.distinctCategories")}</span>
                <span className="summary-value">
                  {formatCount(summary.distinct_categories_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:categoryEconomics.totalRevenue")}</span>
                <span className="summary-value">
                  {formatMoney(summary.total_revenue)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:categoryEconomics.totalBookedMinutes")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_booked_minutes)}
                </span>
              </div>
            </div>

            <div className="highlight-grid">
              <div className="highlight-card">
                <div className="highlight-title">{t("reports:categoryEconomics.topCategoryByRevenue")}</div>
                {topRevenueCategory ? (
                  <>
                    <div className="highlight-name">{topRevenueCategory.category}</div>
                    <div className="highlight-metric">
                      {formatMoney(topRevenueCategory.total_revenue)} PLN
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:categoryEconomics.noData")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:categoryEconomics.topByRevenuePerBookedHour")}</div>
                {topHourlyCategory ? (
                  <>
                    <div className="highlight-name">{topHourlyCategory.category}</div>
                    <div className="highlight-metric">
                      {formatMoney(topHourlyCategory.revenue_per_booked_hour)} PLN/h
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:categoryEconomics.noData")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:categoryEconomics.topByAverageTicket")}</div>
                {topAvgTicketCategory ? (
                  <>
                    <div className="highlight-name">{topAvgTicketCategory.category}</div>
                    <div className="highlight-metric">
                      {formatMoney(topAvgTicketCategory.avg_revenue_per_visit)} PLN/visit
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:categoryEconomics.noData")}</div>
                )}
              </div>
            </div>

            <div className="methodology-card">
              <div className="methodology-title">{t("reports:categoryEconomics.methodology")}</div>
              <ul className="methodology-list">
                {methodologyNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>

              {(loadedPeriodMeta?.from || loadedPeriodMeta?.to) && (
                <div className="loaded-period-note">
                  {t("reports:common.loadedPeriod")}: <strong>{loadedPeriodMeta.from}</strong> →{" "}
                  <strong>{loadedPeriodMeta.to}</strong>
                  {loadedPeriodMeta.displayLabel ? (
                    <> ({loadedPeriodMeta.displayLabel})</>
                  ) : null}
                </div>
              )}
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Executive chart section                                        */}
            {/* -------------------------------------------------------------- */}
            <div className="charts-grid">
              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:categoryEconomics.categoryExcellenceMatrix")}</h2>
                  <p>{t("reports:categoryEconomics.categoryExcellenceMatrixDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 10, right: 24, bottom: 12, left: 8 }}>
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        type="number"
                        dataKey="x"
                        name={t("reports:categoryEconomics.totalRevenue")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${Math.round(value)}`}
                        label={{
                          value: t("reports:categoryEconomics.totalRevenuePln"),
                          position: "insideBottom",
                          offset: -4,
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <YAxis
                        type="number"
                        dataKey="y"
                        name={t("reports:categoryEconomics.revenuePerBookedHour")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        label={{
                          value: t("reports:categoryEconomics.revenuePerBookedHourPln"),
                          angle: -90,
                          position: "insideLeft",
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <ZAxis type="number" dataKey="z" range={[80, 340]} />

                      <Tooltip content={<CategoryExcellenceTooltip />} />
                      <Legend />

                      <ReferenceLine
                        y={120}
                        stroke="#cbd5e1"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <Scatter name={t("reports:categoryEconomics.categories")} data={categoryExcellenceMatrixData}>
                        {categoryExcellenceMatrixData.map((entry) => (
                          <Cell
                            key={`category-excellence-${entry.id}`}
                            fill={entry.color}
                          />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-legend-note">
                  <span className="legend-chip legend-chip--star">{t("reports:categoryEconomics.executiveClasses.star")}</span>
                  <span className="legend-chip legend-chip--efficient">{t("reports:categoryEconomics.executiveClasses.efficient")}</span>
                  <span className="legend-chip legend-chip--volume">{t("reports:categoryEconomics.executiveClasses.volume")}</span>
                  <span className="legend-chip legend-chip--weak">{t("reports:categoryEconomics.executiveClasses.weak")}</span>
                </div>
              </div>

              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:categoryEconomics.categoryRevenueConcentration")}</h2>
                  <p>{t("reports:categoryEconomics.categoryRevenueConcentrationDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={categoryParetoData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 24 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 11 }}
                        angle={-20}
                        textAnchor="end"
                        interval={0}
                        height={72}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${value}%`}
                      />

                      <Tooltip
                        formatter={(value, name) => {
                          if (name === t("reports:categoryEconomics.revenue")) {
                            return [`${formatMoney(value)} PLN`, name];
                          }

                          if (name === t("reports:categoryEconomics.cumulativeShare")) {
                            return [formatPercent(value), name];
                          }

                          return [value, name];
                        }}
                        contentStyle={{
                          backgroundColor: CHART_COLORS.tooltipBg,
                          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />

                      <Legend />

                      <ReferenceLine
                        yAxisId="right"
                        y={80}
                        stroke="#94a3b8"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <Bar
                        yAxisId="left"
                        dataKey="total_revenue"
                        name={t("reports:categoryEconomics.revenue")}
                        radius={[6, 6, 0, 0]}
                      >
                        {categoryParetoData.map((entry, index) => (
                          <Cell
                            key={`category-pareto-${entry.id || index}`}
                            fill={CHART_COLORS.revenue}
                          />
                        ))}
                      </Bar>

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="cumulative_revenue_share_percent"
                        name={t("reports:categoryEconomics.cumulativeShare")}
                        stroke={CHART_COLORS.cumulative}
                        strokeWidth={2}
                        dot={{ r: 2, fill: CHART_COLORS.cumulative }}
                        activeDot={{ r: 5, fill: CHART_COLORS.cumulative }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:categoryEconomics.topCategoryEfficiency")}</h2>
                  <p>{t("reports:categoryEconomics.topCategoryEfficiencyDescription")}</p>
                </div>

                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={categoryEfficiencyRankingData}
                      layout="vertical"
                      margin={{ top: 10, right: 24, left: 24, bottom: 8 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={110}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value) => [
                          `${formatMoney(value)} PLN/h`,
                          t("reports:categoryEconomics.revenuePerBookedHour"),
                        ]}
                        contentStyle={{
                          backgroundColor: CHART_COLORS.tooltipBg,
                          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />
                      <Legend />
                      <Bar
                        dataKey="revenue_per_booked_hour"
                        name={t("reports:categoryEconomics.revenuePerBookedHour")}
                        radius={[0, 8, 8, 0]}
                      >
                        {categoryEfficiencyRankingData.map((entry, index) => (
                          <Cell
                            key={`category-efficiency-${entry.id || index}`}
                            fill={CHART_COLORS.efficiency}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:categoryEconomics.categoryRanking")}</h2>
                <p>{t("reports:categoryEconomics.categoryRankingDescription")}</p>
              </div>

              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:common.category")}</th>
                      <th>{t("reports:categoryEconomics.totalVisits")}</th>
                      <th>{t("reports:categoryEconomics.totalBookedMin")}</th>
                      <th>{t("reports:categoryEconomics.totalRevenuePln")}</th>
                      <th>{t("reports:categoryEconomics.avgRevenuePerVisitPln")}</th>
                      <th>{t("reports:categoryEconomics.avgDurationMin")}</th>
                      <th>{t("reports:categoryEconomics.revenuePerBookedHourPln")}</th>
                      <th>{t("reports:categoryEconomics.distinctServices")}</th>
                      <th>{t("reports:categoryEconomics.topServices")}</th>
                      <th>{t("reports:categoryEconomics.shareOfRevenue")}</th>
                      <th>{t("reports:categoryEconomics.shareOfBookedTime")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length > 0 ? (
                      sortedRows.map((row, idx) => (
                        <tr key={`${row.category}-${idx}`}>
                          <td>{row.category}</td>
                          <td>{formatCount(row.total_visits)}</td>
                          <td>{formatCount(row.total_booked_minutes)}</td>
                          <td>{formatMoney(row.total_revenue)}</td>
                          <td>{formatMoney(row.avg_revenue_per_visit)}</td>
                          <td>{formatMinutes(row.avg_duration_minutes)}</td>
                          <td>{formatMoney(row.revenue_per_booked_hour)}</td>
                          <td>{formatCount(row.distinct_services_count)}</td>
                          <td className="wrap-cell">{formatTopServices(row.top_services)}</td>
                          <td>{formatPercentFromRatio(row.share_of_total_revenue)}</td>
                          <td>{formatPercentFromRatio(row.share_of_total_booked_minutes)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={11} className="empty-table-cell">
                          {t("reports:categoryEconomics.noCategoriesMatchFilters")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}
      </div>
    </div>
  );
};

export default CategoryEconomicsReportPage;

